import { useState, useRef, useEffect, useCallback } from "react";

import {
  EventType,
  InteractionEventTypes,
  usePostSessionDataMutation,
} from "../redux/services/config";

export const useSession = (
  sessionTimeout: number,
  sessionTimeoutNotice: number,
  onSessionTimeout: () => void,
  screenId: string
) => {
  const [postSessionData] = usePostSessionDataMutation();

  const timeoutID = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutNoticeID = useRef<ReturnType<typeof setTimeout> | null>(null);
  const events = useRef<EventType[]>([]);

  const [noticeActive, setNoticeActive] = useState(false);
  const isSessionActive = useRef(false);
  const sessionStart = useRef<string | null>(null);

  // We need to store the onSessionTimeout function as a ref, otherwise the timeout creates a closure and only executes the initial function that was passed in, but the function contains a state variable that could change before the timeout executes.
  const handleSessionTimeout = useRef<typeof onSessionTimeout>();

  useEffect(() => {
    handleSessionTimeout.current = onSessionTimeout;
  }, [onSessionTimeout]);

  const initialiseTimeout = useCallback(() => {
    console.debug(`Initialising "End session" timeout in ${sessionTimeout}ms`);

    return setTimeout(() => {
      console.debug('Executing "End session" timeout');

      if (handleSessionTimeout.current) {
        handleSessionTimeout.current();
      }

      if (sessionStart.current && events.current.length > 0) {
        postSessionData({
          id: screenId,
          session_start: sessionStart.current,
          session_end: new Date().toISOString(),
          component: "map_primary",
          events: events.current,
        });

        events.current = [];
        sessionStart.current = null;
      }

      setNoticeActive(false);
      isSessionActive.current = false;
    }, sessionTimeout);
  }, [postSessionData, sessionTimeout, screenId]);

  const initialiseTimeoutNotice = useCallback(() => {
    console.debug(
      `Initialising "Are you still there?" notice timeout in ${sessionTimeoutNotice}ms`
    );

    return setTimeout(() => {
      console.debug('Executing "Are you still there?" notice timeout');

      setNoticeActive(true);
    }, sessionTimeoutNotice);
  }, [sessionTimeoutNotice]);

  const resetTimeouts = useCallback(() => {
    if (timeoutID.current) clearTimeout(timeoutID.current);
    if (timeoutNoticeID.current) clearTimeout(timeoutNoticeID.current);

    timeoutID.current = initialiseTimeout();
    timeoutNoticeID.current = initialiseTimeoutNotice();

    setNoticeActive(false);
  }, [initialiseTimeout, initialiseTimeoutNotice]);

  const handleTouchEvent = (
    eventType: InteractionEventTypes,
    enableLogging: boolean,
    resetSessionTimeouts: boolean = true,
    extraData?: { [key: string]: any }
  ) => {
    if (!isSessionActive.current) {
      const sesstionStartTime = new Date().toISOString();
      isSessionActive.current = true;
      sessionStart.current = sesstionStartTime;
    }

    if (enableLogging) {
      events.current = [
        ...events.current,
        {
          timestamp: new Date().toISOString(),
          event_type: eventType,
          ...extraData,
        },
      ];
    }

    if (resetSessionTimeouts) {
      resetTimeouts();
    }
  };

  return { handleTouchEvent, noticeActive, resetTimeouts };
};
